import { useActionState } from 'react'
import { formatExpiry } from '@core/utils/format'
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Box,
  Flex,
  Icon,
  Text,
  HStack,
} from '@chakra-ui/react'
import { Lock, Loader2, Clock, Flame } from 'lucide-react'
import type { ShareDetails } from '@/types/share'

interface PasscodePromptProps {
  onSubmit: (passcode: string) => void
  metadata: ShareDetails | null
}

export function PasscodePrompt({ onSubmit, metadata }: PasscodePromptProps) {
  const [_state, formAction, isPending] = useActionState(
    async (_prevState: null, formData: FormData) => {
      const passcode = formData.get('passcode') as string
      if (passcode?.trim()) {
        onSubmit(passcode)
      }
      return null
    },
    null
  )

  return (
    <form action={formAction}>
      <VStack spacing={4}>
        <Box textAlign="center" mb={2}>
          <Flex
            w={16}
            h={16}
            bg="brand.50"
            borderRadius="full"
            align="center"
            justify="center"
            mx="auto"
            mb={4}
          >
            <Icon as={Lock} boxSize={8} color="brand.500" />
          </Flex>
          <Text fontSize="sm" color="stone.500">
            This content is passcode protected. Enter the passcode to view.
          </Text>
        </Box>

        {metadata && (
          <Box w="full" bg="stone.50" borderRadius="lg" p={3} fontSize="sm" color="stone.600">
            <HStack spacing={2} mb={1}>
              <Icon as={Clock} boxSize={4} />
              <Text>Expires: {formatExpiry(metadata.expiresAt)}</Text>
            </HStack>
            {metadata.burnAfterRead && (
              <HStack spacing={2} color="orange.600">
                <Icon as={Flame} boxSize={4} />
                <Text>This share will be deleted after viewing</Text>
              </HStack>
            )}
          </Box>
        )}

        <FormControl>
          <FormLabel>Passcode</FormLabel>
          <Input
            name="passcode"
            type="password"
            placeholder="Enter passcode"
            autoFocus
            isDisabled={isPending}
          />
        </FormControl>

        <Button
          type="submit"
          w="full"
          isLoading={isPending}
          loadingText="Decrypting..."
          leftIcon={isPending ? <Loader2 className="animate-spin" /> : undefined}
        >
          Unlock & View
        </Button>
      </VStack>
    </form>
  )
}
